import React from 'react'
import { createPortal } from 'react-dom'
import styled from '@emotion/styled'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { Icon } from '@mobi/component-library/Assets/Icon'
import type { ProfileSortOrder } from '@core/Areas/BetLoop/Components/SearchProfiles/types'
import { SortButtonBar } from '@core/Areas/BetLoop/Components/Common/SortFeed/Components/SortButtonBar'
import { trackBetLoopSortProfiles } from '@core/Areas/BetLoop/helpers/analytics'

const sortOptions: ProfileSortOption[] = [
  { label: 'Most Followers', order: 'FollowerCount' },
  { label: 'Most Copied', order: 'TotalCopies' },
  { label: 'Most Bets Shared', order: 'TotalShares' },
  { label: 'Most Wins', order: 'TotalWins' },
  { label: 'Longest Active Streak', order: 'ActiveWinStreak' },
  { label: 'Highest Average Fixed Odds', order: 'AvgWinOdds' },
]

export const SortProfiles: React.FC<{
  sortOrder: ProfileSortOrder
  handleSortOrderChange(order: ProfileSortOrder): void
}> = ({ sortOrder, handleSortOrderChange }) => {
  const [isSortTrayOpen, setIsSortTrayOpen] = React.useState(false)

  const selectedOption = getSelectedSortOptionLabel(sortOrder)

  const onSelectSortOption = (option: ProfileSortOption) => () => {
    const selected = sortOptions.find(x => x.label === option.label)
    if (!selected) return

    handleSortOrderChange(option.order)
    window.scrollTo({ top: 0 })
    setIsSortTrayOpen(false)
    trackBetLoopSortProfiles(option.label)
  }

  return (
    <>
      <SortButtonBar
        onClick={() => setIsSortTrayOpen(true)}
        label={selectedOption}
        isOnSearchPage
      />

      {createPortal(
        <Tray
          isOpen={isSortTrayOpen}
          headerTitle='Sort By'
          onDismiss={() => setIsSortTrayOpen(false)}
        >
          {sortOptions.map(option => {
            const isSelected = option.label === selectedOption

            return (
              <SortOptionsListStyled onClick={onSelectSortOption(option)} key={option.label}>
                {option.label}
                {isSelected ? (
                  <Icon name='LineCheck' size='1.8rem' color={colors.lavender['700']} />
                ) : null}
              </SortOptionsListStyled>
            )
          })}
        </Tray>,
        document.body
      )}
    </>
  )
}

// =============
// Local Helpers
// =============

const getSelectedSortOptionLabel = (order: ProfileSortOrder) => {
  const label = sortOptions.find(option => option.order === order)
  return label?.label ?? sortOptions[0].label
}

// =====
// Types
// =====

type ProfileSortLabel =
  | 'Most Followers'
  | 'Most Copied'
  | 'Most Bets Shared'
  | 'Most Wins'
  | 'Longest Active Streak'
  | 'Highest Average Fixed Odds'

type ProfileSortOption = {
  label: ProfileSortLabel
  order: ProfileSortOrder
}

// ======
// Styles
// ======

const SortOptionsListStyled = styled.div({
  height: spacing.lgx1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `0.05rem solid ${colors.neutral['200']}`,
  color: colors.black,
  fontSize: font.size.lg.fontSize,
  lineHeight: font.size.lg.lineHeight,
  fontWeight: font.weight.regular,
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  transition: 'color 0.15s ease, background 0.15s ease',

  '@media (hover: hover) and (pointer: fine)': {
    ':hover': { background: colors.neutral[100] },
  },

  ':active': {
    color: `color(from ${colors.black} srgb r g b / 0.5)`,
    background: colors.neutral[300],
  },
})
